(() => {
  class LittleChecklist extends HTMLElement {
    connectedCallback() {
      this.inputs = [...this.querySelectorAll('input[type="checkbox"]')];
      this.status = this.querySelector('.ll-check-status');
      try {
        const saved = JSON.parse(localStorage.getItem(this.dataset.storageKey) || '[]');
        if (Array.isArray(saved)) this.inputs.forEach(input => { input.checked = saved.includes(input.value); });
      } catch (_) { /* Private browsing can disable storage. The checklist still works. */ }
      this.onChange = () => {
        const checked = this.inputs.filter(input => input.checked);
        this.status.textContent = this.dataset.countText.replace('{checked}', checked.length).replace('{total}', this.inputs.length);
        try { localStorage.setItem(this.dataset.storageKey, JSON.stringify(checked.map(input => input.value))); } catch (_) {}
      };
      this.addEventListener('change', this.onChange);
      this.onChange();
    }
    disconnectedCallback() { this.removeEventListener('change', this.onChange); }
  }
  if (!customElements.get('ll-checklist')) customElements.define('ll-checklist', LittleChecklist);

  class LittlePurchaseBar extends HTMLElement {
    connectedCallback() {
      this.root = document.getElementById(`MainProduct-${this.dataset.section}`);
      this.form = document.getElementById(`product-form-${this.dataset.section}`);
      this.original = this.form?.querySelector('[name="add"]');
      if (!this.form || !this.original || !this.root) return;
      this.button = this.querySelector('[data-bar-submit]');
      this.price = this.querySelector('[data-bar-price]');
      this.onClick = () => {
        if (!this.original.disabled && this.original.getAttribute('aria-disabled') !== 'true') this.form.requestSubmit(this.original);
      };
      this.button.addEventListener('click', this.onClick);
      this.update = () => {
        this.button.disabled = this.original.disabled || this.original.getAttribute('aria-disabled') === 'true';
        this.button.textContent = this.original.querySelector('span')?.textContent.trim() || this.original.textContent.trim();
        const currentPrice = this.root.querySelector('.price:not(.price--on-sale) .price__regular .price-item--regular, .price.price--on-sale .price__sale .price-item--sale');
        if (currentPrice) this.price.textContent = currentPrice.textContent.trim();
      };
      this.mutations = new MutationObserver(() => {
        this.update();
        const error = this.form.closest('product-form')?.querySelector('.product-form__error-message-wrapper:not([hidden])');
        if (error) { this.hidden = true; error.scrollIntoView({block:'center',behavior:'auto'}); }
      });
      this.mutations.observe(this.root, {childList:true,subtree:true,attributes:true,attributeFilter:['disabled','aria-disabled','hidden']});
      this.observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (entry.target === this.form) this.pastForm = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
          else this.footerVisible = entry.isIntersecting;
        }
        this.hidden = !this.pastForm || !!this.footerVisible;
        this.update();
      });
      this.observer.observe(this.form);
      const footer = document.querySelector('.ll-footer');
      if (footer) this.observer.observe(footer);
      this.update();
    }
    disconnectedCallback() { this.observer?.disconnect(); this.mutations?.disconnect(); this.button?.removeEventListener('click', this.onClick); }
  }
  if (!customElements.get('ll-purchase-bar')) customElements.define('ll-purchase-bar', LittlePurchaseBar);

  // Native disclosures remain operable without JavaScript. Escape restores focus.
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    const opened = [...document.querySelectorAll('.ll-header details[open]')];
    const active = opened.findLast(item => item.contains(document.activeElement));
    opened.forEach(item => { item.open = false; });
    active?.querySelector('summary')?.focus();
  });
  document.addEventListener('click', event => {
    document.querySelectorAll('.ll-header details[open]').forEach(item => {
      if (!item.contains(event.target)) item.open = false;
    });
  });
})();

// Progressive enhancement of merchant-authored product descriptions.
// The original HTML remains fully readable when JavaScript is unavailable.
(() => {
  const headingNames = new Set(['features', 'how to use', "what's included", 'what’s included', 'specifications', 'safety information', 'dimensions', 'care instructions', 'size & fit']);
  const normalize = value => value.trim().replace(/:$/, '').toLowerCase();
  function headingText(node) {
    if (node.nodeType !== 1) return '';
    if (/^H[2-6]$/.test(node.tagName)) return node.textContent.trim();
    if (node.tagName === 'P' && node.children.length === 1 && ['STRONG', 'B'].includes(node.firstElementChild.tagName) && node.textContent.trim() === node.firstElementChild.textContent.trim()) return node.textContent.trim();
    return '';
  }
  function enhance(scope = document) {
    scope.querySelectorAll('.product-description').forEach(description => {
      if (description.dataset.llOrganized) return;
      const nodes = [...description.childNodes];
      const markers = nodes.filter(node => headingNames.has(normalize(headingText(node))));
      if (markers.length < 2) return;
      description.dataset.llOrganized = 'true';
      const root = description.closest('[id^="MainProduct-"]');
      if (!root) return;
      // Use the brand explicitly stated in the merchant description when present.
      const brandParagraph = [...description.querySelectorAll('p')].find(p => /^brand:\s*\S/i.test(p.textContent.trim()));
      const vendor = root.querySelector('.vendor');
      if (brandParagraph && vendor && /^by\s/i.test(vendor.textContent.trim())) {
        const brand = brandParagraph.textContent.replace(/^\s*brand:\s*/i, '').trim();
        if (brand.length < 100) vendor.textContent = vendor.textContent.trim().replace(/^(by\s+).*/i, '$1' + brand);
      }
      // If no dedicated summary is supplied, surface one intact sentence from the
      // first prose paragraph. Never generate or infer new product claims.
      if (!root.querySelector('.ll-product-intro')) {
        const prose = nodes.find(node => node.nodeType === 1 && node.tagName === 'P' && !headingText(node) && node.textContent.trim().length > 80);
        const price = root.querySelector('[id^="price-"]');
        if (prose && price) {
          const text = prose.textContent.trim();
          const sentence = text.match(/^.*?[.!?](?:\s|$)/)?.[0].trim() || text;
          if (sentence.length <= 360) {
            const intro = document.createElement('p');
            intro.className = 'll-product-intro ll-derived-intro';
            intro.textContent = sentence;
            price.insertAdjacentElement('afterend', intro);
          }
        }
      }
      const result = document.createDocumentFragment();
      let current = result;
      for (const node of nodes) {
        const label = headingText(node);
        if (headingNames.has(normalize(label))) {
          const details = document.createElement('details');
          details.className = 'll-product-disclosure';
          const summary = document.createElement('summary');
          // Move the existing heading node, retaining text, IDs and links.
          summary.append(node);
          const indicator = document.createElement('span');
          indicator.className = 'll-detail-toggle';
          indicator.setAttribute('aria-hidden', 'true');
          indicator.textContent = '+';
          summary.append(indicator);
          details.append(summary);
          current = document.createElement('div');
          current.className = 'll-answer';
          details.append(current);
          if (normalize(label) === 'safety information') details.open = true;
          result.append(details);
        } else current.append(node);
      }
      description.replaceChildren(result);
      description.classList.add('ll-description-organized');
    });
    scope.querySelectorAll('.ll-product-disclosure:not([data-ll-motion])').forEach(details => {
      details.dataset.llMotion = 'true';
      details.addEventListener('toggle', () => {
        if (!details.open || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        details.querySelector('.ll-answer')?.animate?.([{opacity:0,transform:'translateY(-4px)'},{opacity:1,transform:'translateY(0)'}], {duration:180,easing:'ease-out'});
      });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => enhance(), {once:true});
  else enhance();
  document.addEventListener('shopify:section:load', event => enhance(event.target));
})();
